import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Mail, Shield, ArrowLeft, Eye, EyeOff, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { getSafeAdminReturnPath, rememberAdminReturnPath } from "@/lib/adminRoutePersistence";
import { useAuth } from "@/components/auth/AuthProvider";

type LoginStep = "credentials" | "otp" | "verifying" | "forgot-password" | "reset-password";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser, loading: authLoading } = useAuth();
  const [step, setStep] = useState<LoginStep>("credentials");
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState<Date | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (!otpExpiry) return;

    const interval = setInterval(() => {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000));

      if (remaining === 0) {
        toast.error("OTP expired. Please request a new one.");
        setStep("credentials");
        setOtpExpiry(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [otpExpiry]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown === 0) return;

    const interval = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  // If the shared AuthProvider has already restored an admin session, keep the
  // login form hidden and return the admin to their last console location.
  useEffect(() => {
    if (!authLoading && adminUser) {
      navigate(getSafeAdminReturnPath(), { replace: true });
    }
  }, [authLoading, adminUser, navigate]);

  const resolveEmailFromIdentifier = async (identifier: string): Promise<string | null> => {
    const trimmedIdentifier = identifier.trim();

    // If it contains @, it's an email
    if (trimmedIdentifier.includes("@")) {
      return trimmedIdentifier;
    }

    // Otherwise, it's a username - look up via security-definer RPC (works pre-auth)
    try {
      const { data, error } = await supabase.rpc("get_admin_email_by_username", {
        p_username: trimmedIdentifier,
      });

      if (error) {
        console.error("Username lookup error:", error);
        return null;
      }
      if (!data) return null;
      return data as string;
    } catch (error) {
      console.error("Error resolving username:", error);
      return null;
    }
  };

  const navigateAfterLogin = () => {
    const requestedPath = (location.state as any)?.from?.pathname;
    const requestedSearch = (location.state as any)?.from?.search || "";
    const target = requestedPath?.startsWith("/admin/dashboard")
      ? `${requestedPath}${requestedSearch}`
      : getSafeAdminReturnPath();
    rememberAdminReturnPath(target);
    navigate(target, { replace: true });
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginIdentifier.trim() || !password.trim()) {
      toast.error("Please enter both email/username and password");
      return;
    }

    setLoading(true);
    try {
      const resolvedEmail = await resolveEmailFromIdentifier(loginIdentifier);

      if (!resolvedEmail) {
        toast.error("Invalid email or username");
        return;
      }

      setEmail(resolvedEmail);

      // Verify password BEFORE sending OTP
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: resolvedEmail,
        password,
      });

      if (authError) {
        toast.error("Invalid password. Please check your credentials.");
        return;
      }

      // Immediately sign out after password verification
      await supabase.auth.signOut();

      // Now send OTP since password is correct
      const { data, error } = await supabase.functions.invoke("send-login-otp", {
        body: {
          email: resolvedEmail,
          ipAddress: window.location.hostname,
          userAgent: navigator.userAgent,
        },
      });

      if (error || !data.success) {
        toast.error(data?.error || "Failed to send OTP. Please try again.");
        return;
      }

      setStep("otp");
      setOtpExpiry(new Date(data.expiresAt));
      setResendCooldown(60);
      toast.success(`OTP sent to ${loginIdentifier.includes("@") ? loginIdentifier : resolvedEmail}`);
    } catch (error: any) {
      console.error("Error requesting OTP:", error);
      toast.error("Authentication failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordOnlyLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginIdentifier.trim() || !password.trim()) {
      toast.error("Please enter both email/username and password");
      return;
    }

    setLoading(true);
    try {
      const resolvedEmail = await resolveEmailFromIdentifier(loginIdentifier);

      if (!resolvedEmail) {
        toast.error("Invalid email or username");
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: resolvedEmail,
        password,
      });

      if (authError) throw authError;

      const { data: adminUser, error: adminError } = await supabase
        .from("admin_users")
        .select("*")
        .eq("email", resolvedEmail)
        .eq("is_active", true)
        .maybeSingle();

      if (adminError) throw adminError;

      if (!adminUser) {
        await supabase.auth.signOut();
        toast.error("Admin account not found or not active");
        return;
      }

      toast.success(`Welcome back, ${adminUser.full_name || adminUser.user_name}!`);
      navigateAfterLogin();
    } catch (error: any) {
      console.error("Password login error:", error);
      toast.error(error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    setStep("verifying");

    try {
      // Verify OTP using edge function with service role
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke("verify-login-otp", {
        body: {
          email,
          otp,
          ipAddress: window.location.hostname,
          userAgent: navigator.userAgent,
        },
      });

      if (verifyError || !verifyData?.success) {
        toast.error(verifyData?.error || "Invalid or expired OTP. Please try again.");
        setStep("otp");
        setOtp("");
        return;
      }

      // OTP verified, now sign in with password
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        toast.error("Authentication failed. Please try again.");
        setStep("credentials");
        return;
      }

      const { data: adminUser, error: adminError } = await supabase
        .from("admin_users")
        .select("*")
        .eq("email", email)
        .eq("is_active", true)
        .single();

      if (adminError || !adminUser) {
        await supabase.auth.signOut();
        toast.error("Admin account not found or not active.");
        setStep("credentials");
        return;
      }

      toast.success(`Welcome back, ${adminUser.full_name || adminUser.user_name}!`);
      navigateAfterLogin();
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      toast.error("Verification failed. Please try again.");
      setStep("otp");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-login-otp", {
        body: {
          email,
          ipAddress: window.location.hostname,
          userAgent: navigator.userAgent,
        },
      });

      if (error || !data.success) {
        toast.error(data?.error || "Failed to resend OTP");
        return;
      }

      setOtpExpiry(new Date(data.expiresAt));
      setResendCooldown(60);
      toast.success("New OTP sent to your email");
    } catch (error) {
      toast.error("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setOtp("");
    // Don't reset otpExpiry to preserve timer
  };

  const handleForgotPassword = () => {
    setStep("forgot-password");
    setLoginIdentifier("");
    setPassword("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginIdentifier.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    setLoading(true);
    try {
      const resolvedEmail = await resolveEmailFromIdentifier(loginIdentifier);

      if (!resolvedEmail) {
        throw new Error("Invalid email or username");
      }

      setEmail(resolvedEmail);

      const { data, error } = await supabase.functions.invoke("send-login-otp", {
        body: {
          email: resolvedEmail,
          ipAddress: window.location.hostname,
          userAgent: navigator.userAgent,
        },
      });

      if (error) throw error;

      if (data.success) {
        setOtpExpiry(new Date(data.expiresAt));
        setStep("reset-password");
        toast.success(`Password reset code sent to ${loginIdentifier.includes("@") ? loginIdentifier : resolvedEmail}`);
      } else {
        throw new Error(data.error || "Failed to send OTP");
      }
    } catch (error: any) {
      console.error("Forgot password error:", error);
      toast.error(error.message || "Invalid email or username");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match. Please ensure both password fields match.");
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      toast.error("Password must be at least 8 characters with uppercase, lowercase, number, and special character.");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("reset-password-with-otp", {
        body: {
          email,
          otp: otp.trim(),
          newPassword,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success("Password reset successful! You can now log in with your new password.");

        // Auto-login with new password
        const { error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password: newPassword,
        });

        if (loginError) throw loginError;

        navigateAfterLogin();
      } else {
        throw new Error(data.error || "Failed to reset password");
      }
    } catch (error: any) {
      console.error("Reset password error:", error);
      toast.error(error.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getRemainingTime = () => {
    if (!otpExpiry) return 0;
    const now = new Date();
    return Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000));
  };

  if (authLoading || adminUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
        <div className="flex flex-col items-center gap-4 text-muted-foreground">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm">Opening admin console...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {step === "credentials" && "Admin Login"}
            {step === "otp" && "Verify OTP"}
            {step === "verifying" && "Verifying..."}
            {step === "forgot-password" && "Forgot Password"}
            {step === "reset-password" && "Reset Password"}
          </CardTitle>
          <CardDescription>
            {step === "credentials" && "Enter your credentials to access the admin dashboard"}
            {step === "otp" && "Enter the 6-digit code sent to your email"}
            {step === "verifying" && "Please wait while we verify your credentials"}
            {step === "forgot-password" && "Enter your email to receive a password reset code"}
            {step === "reset-password" && "Enter the code and your new password"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === "credentials" && (
            <div className="space-y-6">
              <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email or Username</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="email or username"
                      value={loginIdentifier}
                      onChange={(e) => setLoginIdentifier(e.target.value)}
                      required
                      className="pl-10"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder=""
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pl-10 pr-10"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Sending OTP..." : "Continue to OTP"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-3"
                  disabled={loading}
                  onClick={handlePasswordOnlyLogin}
                >
                  Login with Password Only (Skip OTP)
                </Button>
              </form>

              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="link"
                  onClick={handleForgotPassword}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot Password?
                </Button>
              </div>
            </div>
          )}

          {step === "forgot-password" && (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="forgotIdentifier" className="text-sm font-medium">
                  Email or Username
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="forgotIdentifier"
                    type="text"
                    value={loginIdentifier}
                    onChange={(e) => setLoginIdentifier(e.target.value)}
                    placeholder="email or username"
                    required
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Sending Code..." : "Send Reset Code"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep("credentials");
                  setLoginIdentifier("");
                }}
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </form>
          )}

          {step === "reset-password" && (
            <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
              <div className="text-sm text-muted-foreground mb-4">
                Code sent to {loginIdentifier.includes("@") ? loginIdentifier : email}
              </div>

              <div className="space-y-2">
                <label htmlFor="resetOtp" className="text-sm font-medium">
                  Verification Code
                </label>
                <Input
                  id="resetOtp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  required
                  disabled={loading}
                />
                {otpExpiry && (
                  <p className="text-xs text-muted-foreground">
                    Code expires in {Math.floor(getRemainingTime() / 60)}:
                    {(getRemainingTime() % 60).toString().padStart(2, "0")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label htmlFor="newPassword" className="text-sm font-medium">
                    New Password
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="text-sm font-semibold mb-2">Password Requirements:</p>
                        <ul className="text-xs space-y-1">
                          <li>• At least 8 characters long</li>
                          <li>• Contains uppercase letter (A-Z)</li>
                          <li>• Contains lowercase letter (a-z)</li>
                          <li>• Contains number (0-9)</li>
                          <li>• Contains special character (@$!%*?&#)</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder=""
                    required
                    disabled={loading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmNewPassword" className="text-sm font-medium">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Input
                    id="confirmNewPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder=""
                    required
                    disabled={loading}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Resetting Password..." : "Reset Password"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep("forgot-password");
                  setOtp("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={handleOTPSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>Code sent to {email}</span>
                </div>

                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {otpExpiry && (
                  <div className="text-center text-sm text-muted-foreground">
                    Code expires in {Math.floor(getRemainingTime() / 60)}:
                    {(getRemainingTime() % 60).toString().padStart(2, "0")}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={loading || otp.length !== 6}>
                  {loading ? "Verifying..." : "Verify & Login"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleResendOTP}
                  disabled={loading || resendCooldown > 0}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBackToCredentials}
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            </form>
          )}

          {step === "verifying" && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Verifying your credentials...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
